import { Link } from '@ipfabric/ui-components/Link/Link';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import type { FC } from 'react';
import {
  type AimFlowPathLookupBody,
  useCalculateDevices,
} from './useCalculateDevices';

export type { AimFlowPathLookupBody };

const CALCULATE_TOOLTIP =
  "Calculate path-lookups for all the application's flows.";

/**
 * Icon form of the calculate trigger, used in the Applications
 * `Flows calculated` cell next to the `{calculated}/{total}` count.
 *
 * Rendered as a `Link` rather than a tertiary `Button` so the glyph takes the
 * link colour of the count beside it — a tertiary button is
 * `text-dark-cornflower-blue`, a shade darker than links. It is still a real
 * `<button>` element; only the styling is a link's.
 *
 * The Flows tab uses `AimCalculateDevicesLink` instead — see that component.
 */
export const AimCalculateDevicesButton: FC<{
  body: AimFlowPathLookupBody;
  /**
   * Why there is nothing to calculate. Disables the trigger and replaces the
   * tooltip, so the control explains itself rather than looking broken.
   */
  unavailableReason?: string;
}> = ({ body, unavailableReason }) => {
  const { calculate, scheduling, disabled } = useCalculateDevices(body);

  return (
    <Tooltip content={unavailableReason ?? CALCULATE_TOOLTIP}>
      {/* Tooltip clones its child to attach the hover handlers, and a disabled
          control is `pointer-events-none` — so without this wrapper the disabled
          state would silently have no tooltip at all. */}
      <span className='inline-flex'>
        <Link
          renderAs='button'
          type='button'
          icon='fa-arrows-rotate'
          onClick={calculate}
          loading={scheduling}
          disabled={disabled || unavailableReason !== undefined}
        />
      </span>
    </Tooltip>
  );
};
